<template>
  <PForm
    ref="pForm"
    :heading="`${invoiceRequestFormState.edit ? 'Edit' : 'Request for'} Invoice`"
    :primary-cta="
      displaySendForApprovalButton ? sendForApprovalButton : submitButton
    "
    @submit.prevent="handleConfirmation"
    @reset="onReset"
    @close="onClose"
    @validation-error="onValidationError"
  >
    <div class="border-none q-ma-none q-pa-none">
      <div class="row q-col-gutter-lg">
        <div class="col-6">
          <PSelectInput
            v-model="formData.bill"
            :options="getPurchaseBillOptions()"
            :validations="{
              label: 'Purchase Bill',
              required: true,
            }"
            label="Select a Purchase Bill"
            option-label="number"
            option-value="_id"
            :emit-value="false"
            :map-options="false"
            placeholder="Select purchase bill"
            @filter="purchaseBillFilter.onFilter"
            @filter-abort="purchaseBillFilter.onAbortFilter"
            @virtual-scroll="purchaseBillFilter.onScroll"
            @update:model-value="populateFormData"
          />
        </div>
        <div class="col-6">
          <PVDateInput
            v-model="formData.pb_date"
            label="Purchase Bill Date"
            placeholder="DD-MM-YYYY"
            :disable="true"
            required
          />
        </div>
        <div class="col-6">
          <PInput
            v-model.trim="formData.vehicle_number"
            label="Vehicle No."
            placeholder="Enter value"
            required
          />
        </div>
        <div class="col-6">
          <PVDateInput
            v-model="formData.date"
            label="Invoice Date"
            placeholder="DD-MM-YYYY"
            required
            :min-date="currentDate"
            :max-date="currentDate"
            :disable="invoiceRequestFormState.edit"
          />
        </div>
        <div class="col-6 column q-col-gutter-xs">
          <PSelectInput
            v-model="unref(formData).billing_address"
            :cta="
              addNewAddressButton('billing', unref(purchaseOrder)?.buyer?._id)
            "
            :disable="invoiceRequestFormState.edit"
            :options="unref(billingAddressFilter.entities)"
            :options-loading="unref(billingAddressFilter.loading)"
            :option-label="
              (val) =>
                getAddressOptionLabel(val, unref(purchaseOrder).buyer?.gstin)
            "
            help="As should be in the e-invoice"
            label="Bill To"
            required
            placeholder="Select an address"
            :rules="[() => addressLengthValidationRule('billing_address_info')]"
            @virtual-scroll="billingAddressFilter.onScroll"
            @filter="billingAddressFilter.onFilter"
            @filter-abort="billingAddressFilter.onFilterAbort"
            @update:model-value="
              (value) =>
                updateAddress(
                  value,
                  'billing_address',
                  unref(purchaseOrder).buyer?.gstin,
                  unref(purchaseOrder).buyer?.shop_name,
                )
            "
          >
            <template #option="{ itemProps, opt }">
              <AddressOption
                :selected="
                  opt._id === unref(formData).billing_address_info?.address_id
                "
                :passed-props="itemProps"
                :address="opt"
                :gstin="unref(purchaseOrder).buyer?.gstin"
              />
            </template>
          </PSelectInput>
          <PInput
            :model-value="tempBillingAddress"
            :disable="true"
            class="col"
            placeholder="Selected billing address"
            type="textarea"
            autogrow
            required
          />
        </div>
        <div class="col-6 q-col-gutter-xs">
          <PSelectInput
            v-model="unref(formData).shipping_address"
            :cta="
              addNewAddressButton('shipping', unref(purchaseOrder)?.buyer?._id)
            "
            :options="unref(shippingAddressFilter.entities)"
            :options-loading="unref(shippingAddressFilter.loading)"
            :option-label="getAddressOptionLabel"
            help="As should be in the e-invoice"
            label="Ship To"
            :rules="[
              () => addressLengthValidationRule('shipping_address_info'),
            ]"
            required
            placeholder="Select an address"
            @virtual-scroll="shippingAddressFilter.onScroll"
            @filter="shippingAddressFilter.onFilter"
            @filter-abort="shippingAddressFilter.onFilterAbort"
            @update:model-value="
              (value) =>
                updateAddress(
                  value,
                  'shipping_address',
                  unref(purchaseOrder).buyer?.gstin,
                  unref(purchaseOrder).buyer?.shop_name,
                )
            "
          >
            <template #option="{ itemProps, opt }">
              <AddressOption
                :selected="
                  opt._id === unref(formData).shipping_address_info?.address_id
                "
                :passed-props="itemProps"
                :address="opt"
                :gstin="unref(purchaseOrder).buyer?.gstin"
              />
            </template>
          </PSelectInput>
          <PInput
            :model-value="tempShippingAddress"
            :disable="true"
            placeholder="Selected shipping address"
            required
            type="textarea"
            autogrow
          />
        </div>
        <div class="col-6 column">
          <PSelectInput
            v-model="unref(formData).dispatch_address"
            :cta="
              addNewAddressButton(
                'dispatch',
                unref(selectedPurchaseBill)?.seller?._id,
              )
            "
            :rules="[
              () => addressLengthValidationRule('dispatch_address_info'),
            ]"
            :disable="invoiceRequestFormState.edit"
            :options="unref(sellerDispatchAddressFilter.entities)"
            :options-loading="unref(sellerDispatchAddressFilter.loading)"
            :option-label="getAddressOptionLabel"
            help="Mention the dispatch location"
            label="Dispatch From"
            required
            placeholder="Select an address"
            @virtual-scroll="sellerDispatchAddressFilter.onScroll"
            @filter="sellerDispatchAddressFilter.onFilter"
            @filter-abort="sellerDispatchAddressFilter.onFilterAbort"
            @update:model-value="
              (value) =>
                updateAddress(
                  value,
                  'dispatch_address',
                  null,
                  unref(selectedPurchaseBill)?.seller?.shop_name,
                )
            "
          >
            <template #option="{ itemProps, opt }">
              <AddressOption
                :selected="
                  opt._id === unref(formData).dispatch_address_info?.address_id
                "
                :passed-props="itemProps"
                :address="opt"
              />
            </template>
          </PSelectInput>
          <PInput
            v-model.trim="unref(formData).dispatch_address"
            :disable="true"
            class="col"
            placeholder="Selected dispatch address"
            type="textarea"
            autogrow
          />
        </div>
      </div>
    </div>
    <q-separator v-if="formData.bill_id" class="q-my-xl" />
    <div v-if="formData.bill_id" class="border-none q-ma-none q-pa-none">
      <div class="row q-col-gutter-lg">
        <div class="col-12">
          <PField
            v-model="formData.have_addl_bills"
            label="Do you want to add items from additional purchase bills?"
            help="
            Use this feature to add items from multiple purchase bills.
            Note that multiple purchase bills are only allowed for a vendor of the selected purchase bill above while requesting this invoice.
            "
            help-on-hover
          >
            <template #control>
              <POptionGroup
                v-model="formData.have_addl_bills"
                :options="[
                  {
                    description: 'Default',
                    label: 'No',
                    value: false,
                  },
                  {
                    description: 'Multiple bills',
                    label: 'Yes',
                    value: true,
                  },
                ]"
                @update:model-value="updateLineItems"
              />
            </template>
          </PField>
        </div>
        <div v-if="formData.have_addl_bills" class="col-12">
          <PSelectInput
            v-model="formData.addl_bills"
            :emit-value="false"
            :map-options="false"
            :options="getAdditionalPurchaseBillOptions()"
            :options-loading="unref(vendorPurchaseBillFilter.loading)"
            label="Select additional Purchase Bills"
            option-label="number"
            option-value="_id"
            placeholder="Select purchase bills"
            multiple
            reactive-rules
            use-chips
            virtual-scroll-slice-size="25"
            virtual-scroll-item-size="53"
            @virtual-scroll="vendorPurchaseBillFilter.onScroll"
            @filter="vendorPurchaseBillFilter.onFilter"
            @filter-abort="vendorPurchaseBillFilter.onAbortFilter"
            @update:model-value="updateAdditionalBills"
          >
            <template #no-option>
              <q-item class="bg-primary-20 text-neutral-80">
                <q-item-section> No additional bills found </q-item-section>
              </q-item>
            </template>
          </PSelectInput>
        </div>
      </div>
    </div>
    <q-separator class="q-mb-xl q-mt-sm" />
    <div class="overflow-hidden">
      <div class="row justify-between">
        <legend class="q-mb-sm text-neutral text-weight-medium text-md">
          Items
        </legend>
        <div v-if="formData.lineitems.length">
          <span class="text-sm">Sales Margin:</span>
          <PBadge
            v-for="marginType in marginTypes"
            :key="marginType"
            :label="marginType"
            :type="marginType"
            class="q-ml-xs"
          />
        </div>
      </div>

      <q-separator v-if="formData?.lineitems?.length" />

      <PTable
        v-if="formData.lineitems.length"
        :pagination="pagination"
        rows-per-page-label=" "
        :rows-per-page-options="[]"
        :columns="productListColumns"
        :rows="formData.lineitems"
        :style="{ margin: '0 -15px', padding: 0 }"
        table-header-class="text-neutral-70 bg-neutral-10"
        dense
        flat
        wrap-cells
      >
        <template #body="props">
          <q-tr :props="props" :class="getMarginClasses(props.row)">
            <q-td v-for="col in props.cols" :key="col.name" :props="props">
              <ItemNameCell
                v-if="col.name === 'name'"
                :col="col"
                :table-props="props"
              />

              <ItemQuantityCell
                v-else-if="col.name === 'qty'"
                :col="col"
                :table-props="props"
              />

              <ItemRateCell
                v-else-if="col.name === 'rate'"
                :col="col"
                :table-props="props"
              />

              <ItemAmountCell
                v-else-if="col.name === 'amount'"
                :col="col"
                :table-props="props"
              />

              <div
                v-else-if="col.name === 'action'"
                class="align-middle flex no-wrap justify-center"
              >
                <PBtn
                  color="primary"
                  dense
                  flat
                  fluid
                  rounded
                  title="Edit Item"
                  @click="
                    editItem({
                      item: props.row,
                      index: props.rowIndex,
                      entityType: 'E-Invoice',
                    })
                  "
                >
                  <EditIcon class="icon--sm" />
                </PBtn>
                <PBtn
                  dense
                  flat
                  rounded
                  title="Delete Item"
                  @click="removeProduct(props.rowIndex)"
                >
                  <RemoveIcon class="icon--sm" />
                </PBtn>
              </div>

              <PEditable
                v-else
                :model-value="col.value"
                :type="col.type"
                disable
              />
            </q-td>
          </q-tr>
        </template>
      </PTable>
      <span v-else-if="formData.bill_id" class="text-negative">
        At least 1 product is required
      </span>
      <span v-else class="text-neutral-50">No Items</span>
    </div>
    <PBtn
      v-if="!invoiceRequestFormState.view"
      :fluid="false"
      :loading="false"
      class="q-mt-md"
      dense
      outline
      :disable="!formData.bill_id"
      @click="onAddDiscountItems"
    >
      <AddIcon class="icon--sm q-mr-xxs" />
      <span>Add Discount/Insurance</span>
    </PBtn>
    <q-separator class="q-mb-xl q-mt-sm" />
    <div class="border-none q-ma-none q-pa-none">
      <div class="row q-col-gutter-lg">
        <div class="col-6">
          <PInput
            v-model.trim="formData.remarks"
            label="Remarks (if any)"
            placeholder="Enter remarks"
            type="textarea"
            :required="displaySendForApprovalButton"
            autogrow
          />
        </div>
        <div class="col-6">
          <div class="row q-col-gutter-lg items-center">
            <div class="col-6">
              <span class="text-neutral"> Overall Margin </span>
            </div>
            <div class="col-6">
              <div class="text-right" :class="overallMarginClasses">
                {{ overallMargin.sales + "%" }}
              </div>
            </div>
            <div class="col-6">
              <span class="text-neutral"> Overall Expected Margin </span>
            </div>
            <div class="col-6">
              <div class="text-right">
                {{ overallMargin.expected + "%" }}
              </div>
            </div>

            <ServiceItemList
              v-if="isVasApplicableOnBuyer"
              :vas-items="formData.vas_items"
              class="col-12"
              @add="addServiceItem"
              @remove="removeVasItem"
            />
          </div>
        </div>
      </div>
    </div>
    <q-separator class="q-my-xl" />
    <fieldset class="border-none q-ma-none q-pa-none">
      <legend class="q-mb-sm text-neutral text-weight-medium">
        Note: Products are converted to PO unit, in case of difference in units
        between PO and PB
      </legend>

      <DealSettingMessage
        v-if="!dealSettingExist"
        v-bind="dealSettingMessageProps"
      />
      <p
        v-if="isMarginBelowExpectation"
        class="text-error-60 text-weight-medium"
      >
        Note: Requesting an Invoice is disabled because your sales margin
        {{ overallMargin.sales }}% is below the expected margin
        {{ overallMargin.expected }}% for customer
        {{ purchaseOrder?.buyer?.shop_name }}. Reach out to product support for
        help.
      </p>
    </fieldset>

    <div ref="oversellingBanner">
      <OversellingBanner
        :is-send-for-approval-button-visible="displaySendForApprovalButton"
        :is-approval-pending="invoiceOversellingPendingApproval"
        :items="oversellingLineitems"
      />
    </div>
  </PForm>
</template>

<script setup>
import { useQuasar, date } from "quasar";
import EditIcon from "assets/icons/edit.svg";
import RemoveIcon from "assets/icons/remove.svg";
import AddIcon from "assets/icons/add.svg";
import PConfirmDialog from "src/components/PConfirmDialog.vue";

import invoiceRequestFormModel from "src/modules/purchase-order/models/invoiceRequestForm";
import { PRODUCT_MARGIN } from "src/assets/scripts/utilities/constants";
import { useDealSettingsExist } from "src/modules/deal/composables/dealSettingsExist";
import { notify } from "assets/scripts/utilities/api-clients";
import { useCategories } from "src/composables/categories";

const $q = useQuasar();
const pForm = ref(null);
const currentDate = new Date();

const { invoiceRequestFormState, invoice } = storeToRefs(useInvoiceStore());
const { purchaseOrder } = storeToRefs(usePurchaseOrderStore());
const { getPurchaseOrder } = usePurchaseOrderStore();

const { toggleAddAddressForm } = useAddressStore();

const { fetchCategories, categories } = useCategories({
  searchCategories: ["Service", "Discount"],
});

let purchaseBillFilter = ref({});
const tempBillingAddress = ref("");
const tempShippingAddress = ref("");

watchEffect(() => {
  purchaseBillFilter.value = useFilter({
    entity: "purchaseBill",
    options: {
      params: {
        po_id: unref(purchaseOrder)?._id,
        status_cd: "active",
      },
    },
  });
});

const confirmationMessage = computed(() => {
  let invcDate = date.formatDate(unref(formData).date, "DD-MM-YY");

  return `<p class="text-left">
          This invoice will be requested for the date
            <span class="text-weight-demibold" style="max-width:150px;">
              ${invcDate}
            </span>. If you want
            <br/>
            to change this date, Please change it from Invoice date field.
          </p>
          <p class="text-left">Are you sure want to proceed?</p>
      `;
});

const validateAddressLength = (address) => {
  return address?.length <= 100;
};

const addressLengthValidationRule = (type) => {
  const value = unref(formData)[`${type}`]?.address_line;

  if (!validateAddressLength(value)) {
    return "";
  }

  return true;
};

const validateAddressLengths = () => {
  const errorMsg = "Address Line 1 exceeds 100 characters: ";
  const note = ". Please correct it.";

  const billing_address_line =
    unref(formData).billing_address_info?.address_line || "";

  if (!validateAddressLength(billing_address_line)) {
    const msg = "Billing " + errorMsg + " " + billing_address_line + note;

    return msg;
  }

  const shipping_address_line =
    unref(formData).shipping_address_info?.address_line || "";

  if (!validateAddressLength(shipping_address_line)) {
    const msg = "Shipping " + errorMsg + " " + shipping_address_line + note;

    return msg;
  }

  const dispatch_address_line =
    unref(formData).dispatch_address_info?.address_line || "";

  if (!validateAddressLength(dispatch_address_line)) {
    const msg = "Dispatch " + errorMsg + " " + dispatch_address_line + note;

    return msg;
  }

  return null;
};

async function handleConfirmation() {
  if (unref(displaySendForApprovalButton)) {
    $q.dialog({
      component: PConfirmDialog,
      componentProps: {
        title: "Send for Approval",
        text: "Are you sure want to send this Invoice Request for Overselling approval?",
        labelClass: "",
        isFooterVisible: true,
        submitLabel: "Send",
        submittable: true,
        cancelLabel: "Cancel",
        cancellable: true,
        closable: true,
        onConfirm: onSubmitApprovalRequest,
      },
    });
  } else {
    $q.dialog({
      component: PConfirmDialog,
      componentProps: {
        htmlContent: unref(confirmationMessage),
        isFooterVisible: true,
        submitLabel: "Submit",
        submittable: true,
        cancelLabel: "Cancel",
        cancellable: true,
        closable: true,
        onConfirm: onSubmit,
      },
    });
  }
}

async function onValidationError() {
  const invalidAddressMsg = validateAddressLengths();

  if (invalidAddressMsg) {
    return notify({
      type: "negative",
      message: invalidAddressMsg,
      timeout: 100000,
      actions: [
        {
          label: "Dismiss",
          color: "Red",
        },
      ],
    });
  }
}

const getPurchaseBillOptions = () => {
  return unref(unref(purchaseBillFilter).entities)?.filter((bill) => {
    return !unref(formData)
      ?.addl_bills.map((bill) => bill?._id)
      ?.includes(bill?._id);
  });
};

const addNewAddress = (addressType, userId) => {
  if (!userId) {
    return;
  }

  toggleAddAddressForm({
    open: true,
    level: 0,
    props: {
      documentType: "Invoice",
      userId: userId,
      addressType: addressType,
      updateParent: selectAddress,
    },
  });
};

const selectAddress = (newAddressData) => {
  if (!newAddressData) {
    return;
  }

  if (newAddressData.type === "billing") {
    updateAddress(
      newAddressData,
      "billing_address",
      unref(purchaseOrder).buyer?.gstin,
      unref(purchaseOrder).buyer?.shop_name,
    );
  } else if (newAddressData.type === "shipping") {
    updateAddress(
      newAddressData,
      "shipping_address",
      unref(purchaseOrder).buyer?.gstin,
      unref(purchaseOrder).buyer?.shop_name,
    );
  } else if (newAddressData.type === "dispatch") {
    updateAddress(
      newAddressData,
      "dispatch_address",
      null,
      unref(selectedPurchaseBill)?.seller?.shop_name,
    );
  }
};

const addNewAddressButton = (addressType, userId) => {
  return {
    dense: true,
    flat: true,
    fluid: true,
    text: true,
    icon: "sym_r_add",
    label: `Add New Address`,
    disabled: !unref(formData).bill_id,
    onClick: () => addNewAddress(addressType, userId),
  };
};

let vendorPurchaseBillFilter = ref({});

const initVendorPurchaseBillFilter = () => {
  vendorPurchaseBillFilter.value = useFilter({
    entity: "purchaseBill",
    options: {
      params: {
        po_id: unref(purchaseOrder)?._id,
      },
    },
  });
};

const getAdditionalPurchaseBillOptions = () => {
  return unref(unref(vendorPurchaseBillFilter).entities)?.filter(
    (bill) => bill?._id !== unref(formData).bill_id,
  );
};

const { postInvoice, putInvoice, toggleInvoiceRequestForm } = useInvoiceStore();

const { formData, isLoading, closeForm, resetForm, submitForm } = useForm({
  formModel: invoiceRequestFormModel(),
  entity: invoice,
  formRef: pForm,
  isForm: invoiceRequestFormState,
  post: postInvoice,
  put: putInvoice,
  toggleForm: toggleInvoiceRequestForm,
});

const {
  marginTypes,
  overallMargin,
  overallMarginClasses,
  pagination,
  editItem,
  getConvertedQty,
  getConvertedRate,
  getMarginClasses,
  getSalesMargin,
  removeProduct,
  updateProductRate,
  calculateTcs,
  addItem,
} = useProductList({
  formData,
  purchaseOrder,
  excludedCategories: categories,
});

const {
  oversellingLineitems,
  invoiceOversellingPendingApproval,
  sendForApprovalSuppressed,
  displaySendForApprovalButton,
  sendForApprovalButton,
  validateLineitemsForOverselling,
  sendApprovalRequest,
  resetOversellingLineitems,
} = useOverselling({
  formData,
});

const preSubmissionDataValidationAndPopulation = async () => {
  if (!unref(formData).lineitems?.length) {
    return;
  }

  await getPurchaseOrder(unref(purchaseOrder)?._id);

  const { buyer } = unref(purchaseOrder);

  unref(formData).buyer_gstin = buyer?.gstin;

  unref(formData).buyer_name = buyer.name;

  unref(formData).po_info.buyer_gstin = buyer?.gstin;

  unref(formData).po_info.buyer_name = buyer.name;

  calculateTcs(unref(formData).total_amount);
};

const onSubmit = async () => {
  await preSubmissionDataValidationAndPopulation();

  isLoading.value = true;

  const isOverselling = await validateLineitemsForOverselling();

  isLoading.value = false;

  if (isOverselling) {
    return;
  }

  await submitForm();

  clearTempAddresses();
};

const onSubmitApprovalRequest = async () => {
  await preSubmissionDataValidationAndPopulation();

  await sendApprovalRequest();

  onClose();
};

const productListColumns = computed(() => {
  let columns = [
    {
      align: "left",
      name: "name",
      label: "Name",
      field: "name",
    },
    {
      align: "left",
      name: "qty",
      label: "Qty",
      field: "qty",
    },
    {
      align: "right",
      name: "rate",
      label: "Rate",
      field: "rate",
      type: "currency",
    },
    {
      align: "right",
      name: "amount",
      label: "Total",
      field: "amount",
      type: "currency",
    },
    {
      align: "left",
      name: "sales_margin",
      label: "Sales Margin",
      field: getSalesMargin,
      format: (salesMargin = 0) => `${salesMargin}%`,
    },
    {
      align: "left",
      name: "margin_expectation",
      label: "Expected Margin (Min)",
      field: (row) =>
        row?.margin_expectation?.min || PRODUCT_MARGIN.default.min,
      format: (minMarginExpectation) =>
        minMarginExpectation ? `${minMarginExpectation}%` : "-",
    },
    {
      align: "right",
      name: "converted_rate",
      label: "Conv. PB Rate (Excl. Tax)",
      field: getConvertedRate,
      format: (convertedRate) => convertedRate || "-",
      type: "currency",
    },
    {
      align: "center",
      classes: "sticky--right",
      headerClasses: "sticky--right",
      name: "action",
      label: "Action",
    },
  ];

  return columns;
});

const selectedPurchaseBill = ref({});

const billHasLinkedPOs = computed(() => {
  return unref(selectedPurchaseBill)?.purchase_orders?.length > 1;
});

const populateLineItemData = (lineItem, index) => {
  let { cess, gst, po_rate, po_unit, qty, rate, unit } = lineItem;

  if (lineItem.additional || unref(billHasLinkedPOs)) {
    const poLineItem = unref(purchaseOrder)?.lineitems?.find(
      (poLineItem) => poLineItem?.item_id === lineItem?.item_id,
    );

    if (poLineItem) {
      po_rate = poLineItem.rate;

      po_unit = poLineItem.unit;
    }

    delete lineItem.additional;
  }

  lineItem = {
    ...lineItem,
    pb_cess: Number(cess),
    pb_gst: Number(gst),
    pb_qty: Number(qty),
    pb_rate: Number(rate),
    pb_unit: unit,

    qty: getConvertedQty(lineItem),

    rate: Number(po_rate),
    rate_wt: Number(po_rate) + Number(po_rate) * ((gst + cess) / 100),

    unit: po_unit,
  };

  nextTick(() => {
    updateProductRate(lineItem, index);
  });

  return lineItem;
};

let billingAddressFilter = useFilter({
  entities: [],
});

let shippingAddressFilter = useFilter({
  entities: [],
});

let sellerDispatchAddressFilter = useFilter({
  entities: [],
});

const getAddressOptionLabel = (address, gstin) => {
  if (!address) return;
  else if (typeof address === "string") {
    return address;
  } else if (typeof address === "object") {
    const addressLine = address.address_line || address.address;

    return (
      (addressLine ? addressLine + ", " : "") +
      (address.city ? address.city + ", " : "") +
      (address.state ? address.state + ", " : "") +
      (address.pincode ? address.pincode + " " : "") +
      (gstin ? "\nGSTIN: " + gstin : "")
    );
  }
};

const isVasApplicableOnBuyer = ref(false);

const { addVasItem, populateVasItems, removeVasItem } = useVASItems({
  formData,
});

const addServiceItem = () => {
  addVasItem({ entityType: "E-Invoice", categories: ["VAS"] });
};

const { dealSetting, dealSettingExist, fetchDealSettings } =
  useDealSettingsExist();

const populateFormData = async (purchaseBill) => {
  clearAddresses();

  selectedPurchaseBill.value = purchaseBill;

  const { date, freight, lineitems, total_amount, total_tax, vehicle_number } =
    unref(selectedPurchaseBill);

  const {
    billing_address,
    number,
    internal_number,
    buyer,
    payment_terms,
    shipping_address,
    billing_address_info,
    shipping_address_info,
  } = unref(purchaseOrder);

  const { name, gstin, is_vas_applicable = false } = buyer;

  isVasApplicableOnBuyer.value = is_vas_applicable;

  billingAddressFilter = useFilter({
    entity: "address",
    options: {
      params: {
        get_primary: true,
        type: "billing",
        user_id: unref(purchaseOrder)?.buyer?._id,
      },
    },
  });

  shippingAddressFilter = useFilter({
    entity: "address",
    options: {
      params: {
        get_primary: true,
        type: "shipping",
        user_id: unref(purchaseOrder)?.buyer?._id,
      },
    },
  });

  sellerDispatchAddressFilter = useFilter({
    entity: "address",
    options: {
      params: {
        get_primary: true,
        type: "dispatch",
        user_id: unref(selectedPurchaseBill)?.seller?._id,
      },
    },
  });

  formData.value = {
    ...unref(formData),
    bill_id: purchaseBill._id,
    billing_address,
    buyer: buyer?._id,
    buyer_name: name,
    buyer_gstin: gstin,
    pb_date: date,
    date: new Date(),
    freight: freight || 0,
    lineitems: lineitems?.map(populateLineItemData),
    payment_terms,
    po_id: unref(purchaseOrder)?._id,
    tcs:
      buyer?.tax_settings?._id == null
        ? null
        : {
            code: buyer.tax_settings?.code,
            percentage: buyer.tax_settings?.percentage,
            amount: (buyer.tax_settings?.percentage / 100) * total_amount,
          },
    po_info: {
      number,
      internal_number,
      buyer: buyer?._id,
      buyer_name: name,
      buyer_gstin: gstin,
      payment_terms,
    },
    shipping_address,
    total_amount,
    total_tax,
    vehicle_number,
    billing_address_info,
    shipping_address_info,
  };

  if (is_vas_applicable) {
    populateVasItems();
  }

  await fetchDealSettings({
    buyerId: unref(formData)?.po_info?.buyer,
    sellerId: unref(selectedPurchaseBill).seller?._id,
  });

  formData.value.deal_setting = unref(dealSetting);

  tempBillingAddress.value =
    unref(purchaseOrder).buyer?.shop_name + "\n" + billing_address;

  tempShippingAddress.value =
    unref(purchaseOrder).buyer?.shop_name + "\n" + shipping_address;

  initVendorPurchaseBillFilter();
};

const dealSettingMessageProps = computed(() => {
  return {
    buyerName: unref(purchaseOrder).buyer?.shop_name || "",
    sellerName: unref(selectedPurchaseBill)?.seller?.shop_name || "",
    buyerGstin: unref(formData)?.po_info?.buyer_gstin || "",
    sellerGstin: unref(selectedPurchaseBill)?.seller?.gstin || "",
  };
});

const clearAddresses = () => {
  unref(formData).billing_address = "";

  unref(formData).shipping_address = "";

  unref(formData).dispatch_address = "";

  unref(formData).billing_address_info = {};

  unref(formData).shipping_address_info = {};

  unref(formData).dispatch_address_info = {};

  clearTempAddresses();
};

const updateAddress = (info, addressType, gstin, shopName) => {
  if (!info) {
    return;
  }

  unref(formData)[`${addressType}_info`] = {
    city: info.city,
    state: info.state,
    pincode: info.pincode,
    address_line: info.address_line,
    state_code: info.state_code,
    shop_name: shopName,
    address_id: info?._id,
  };

  const gstinText = gstin ? `\nGSTIN: ${gstin}` : "";
  const concatenatedAddress = `${info.address_line ? info.address_line + ", " : ""}${info.city ? info.city + ", " : ""}${info.state ? info.state + ", " : ""}${info.pincode ? info.pincode + " " : ""}${gstinText}`;

  unref(formData)[`${addressType}`] = concatenatedAddress;

  if (addressType === "billing_address") {
    tempBillingAddress.value = `${shopName}\n${concatenatedAddress}`;
  } else if (addressType === "shipping_address") {
    tempShippingAddress.value = `${shopName}\n${concatenatedAddress}`;
  }
};

const updateLineItems = (additionalBills) => {
  const bill = unref(selectedPurchaseBill);

  let lineItems = bill?.lineitems || [];

  if (!unref(formData).have_addl_bills) {
    additionalBills = [];

    unref(formData).addl_bills = [];
  }

  let additionalLineItems =
    Array.isArray(additionalBills) &&
    additionalBills?.reduce((lineitems, bill) => {
      return [...lineitems, ...bill.lineitems];
    }, []);

  if (additionalLineItems) {
    additionalLineItems = additionalLineItems.map((lineItem) => {
      lineItem.additional = true;

      return lineItem;
    });

    lineItems = [...lineItems, ...additionalLineItems];

    unref(formData).lineitems = lineItems.map(populateLineItemData);
  }

  sendForApprovalSuppressed.value = true;
};

const updateAdditionalBills = (additionalBills) => {
  unref(formData).addl_bills = additionalBills.map((additionalBill) => {
    delete additionalBill.po_id;

    additionalBill.po_id = additionalBill?.purchase_orders?.map(
      (po) => po?._id,
    );

    delete additionalBill.purchase_orders;

    additionalBill.created_by = additionalBill?.created_by?._id;

    return additionalBill;
  });

  updateLineItems(additionalBills);
};

const isMarginBelowExpectation = computed(() => {
  return unref(overallMargin).sales < unref(overallMargin).expected;
});

const disableSubmitButton = computed(() => {
  if (!unref(dealSettingExist)) {
    return true;
  }

  return (
    !unref(formData)?.lineitems?.length ||
    !unref(formData).total_amount ||
    unref(isMarginBelowExpectation)
  );
});

const submitButton = computed(() => {
  return {
    disable: unref(disableSubmitButton),
    loading: unref(isLoading),
  };
});

const clearTempAddresses = () => {
  tempBillingAddress.value = "";

  tempShippingAddress.value = "";
};

const onReset = () => {
  resetForm();

  clearTempAddresses();

  resetOversellingLineitems();

  isVasApplicableOnBuyer.value = false;
};

const onClose = () => {
  closeForm();

  clearTempAddresses();

  resetOversellingLineitems();

  isVasApplicableOnBuyer.value = false;
};

const onAddDiscountItems = async () => {
  addItem({ entityType: "E-Invoice", categories: ["Discount", "Insurance"] });
};

watchEffect(() => {
  if (unref(invoiceRequestFormState).open) {
    fetchCategories();
  }
});
</script>
